import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Settings } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";
import { observer } from "mobx-react";
import { RootContext } from "../context";
import InputField from "./commonComponents/InputField";
import { toastManager } from "../utils/toastManager";
interface FormInputs {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePassword: React.FC = observer(() => {
  const { authStore } = useContext(RootContext);
  const { isAuthenticated, requestChangePassword } = authStore;
  const navigate = useNavigate();

  const { register, handleSubmit, watch, formState: { errors, isSubmitting }, reset } = useForm<FormInputs>();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    try {
      await requestChangePassword(data.currentPassword, data.newPassword);
      toastManager.success('Password changed successfully');
      reset();
    } catch (error: unknown) {
      toastManager.handleError(error);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Change Password
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage your password</p>
          </div>
        </div>
      </div>
      
    <div className="form-wrapper">
      <form onSubmit={handleSubmit(onSubmit)} className="bg-white w-full max-w-3xl">
        <h2 className="text-xl md:text-2xl font-semibold md:font-bold mb-6 text-gray-800">Change Password</h2>
        <div className="relative mb-4">
          <InputField
            label="Current Password"
            name="currentPassword"
            register={register("currentPassword", {
              required: "Current password is required",
            })}
            type={showCurrentPassword ? "text" : "password"}
            error={errors.currentPassword?.message}
            placeholder="Enter your current password"
            disable={isSubmitting}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('current')}
            className="absolute right-3 top-9 text-gray-500 focus:outline-none"
          >
            {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <div className="relative mb-4">
          <InputField
            label="New Password"
            name="newPassword"
            register={register("newPassword", {
              required: "New password is required",
            })}
            type={showNewPassword ? "text" : "password"}
            error={errors.newPassword?.message}
            placeholder="Enter your new password"
            disable={isSubmitting}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-9 text-gray-500 focus:outline-none"
          >
            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <div className="relative mb-4">
          <InputField
            label="Confirm New Password"
            name="confirmNewPassword"
            register={register("confirmNewPassword", {
              required: "Please confirm your new password",
              validate: (value) => value === watch("newPassword") || "Passwords don't match"
            })}
            type={showConfirmPassword ? "text" : "password"}
            error={errors.confirmNewPassword?.message}
            placeholder="Re-enter your new password"
            disable={isSubmitting}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-9 text-gray-500 focus:outline-none"
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <div className="flex justify-between gap-4">
          <Link to={'/'}>
            <button
              type="button"
              disabled={isSubmitting}
              className={`border-red-500 border text-red-900 p-2 px-4 rounded-md mt-4 
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-500 hover:text-white'}`}
            >
              Cancel
            </button>
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-blue-500 text-white p-2 px-4 rounded-md mt-4 
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
          >
            {isSubmitting ? 'Changing password...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
});

export default ChangePassword;